import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { 
  Plus, Edit2, Trash2, Search, ChevronLeft, ChevronRight, 
  Star, AlertCircle, CheckCircle, Loader2, Tag, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Color options for status
const COLOR_OPTIONS = [
  { value: '#3B82F6', label: 'Blue' },
  { value: '#8B5CF6', label: 'Purple' },
  { value: '#10B981', label: 'Green' },
  { value: '#F59E0B', label: 'Amber' },
  { value: '#EC4899', label: 'Pink' },
  { value: '#22C55E', label: 'Emerald' },
  { value: '#EF4444', label: 'Red' },
  { value: '#6B7280', label: 'Gray' },
  { value: '#14B8A6', label: 'Teal' },
  { value: '#F97316', label: 'Orange' },
];

export default function LeadStatusManagement() {
  const { token } = useAuth();
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#3B82F6',
    sort_order: 0,
    is_active: true
  });

  // Sync from HubSpot
  const syncFromHubSpot = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/integrations/hubspot/sync-master-values?token=${token}`);
      if (response.data.success) {
        const leadStats = response.data.lead_statuses || {};
        toast.success(`HubSpot sync complete: ${leadStats.created || 0} created, ${leadStats.updated || 0} updated`);
        fetchStatuses();
      } else {
        toast.error('Failed to sync from HubSpot');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to sync from HubSpot';
      toast.error(message);
    } finally {
      setSyncing(false);
    }
  };

  const fetchStatuses = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        token,
        page: page.toString(),
        limit: '20',
        include_inactive: includeInactive.toString()
      });
      if (search) params.append('search', search);
      
      const response = await axios.get(`${API}/master/lead-statuses?${params}`);
      if (response.data.success) {
        setStatuses(response.data.statuses);
        setTotalPages(response.data.pages);
        setTotal(response.data.total);
      }
    } catch (error) {
      console.error('Failed to fetch lead statuses:', error);
      toast.error('Failed to load lead statuses');
    } finally {
      setLoading(false);
    }
  }, [token, page, search, includeInactive]);

  useEffect(() => {
    fetchStatuses();
  }, [fetchStatuses]);

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast.error('Status name is required');
      return;
    }
    
    setSaving(true);
    try {
      const response = await axios.post(`${API}/master/lead-statuses?token=${token}`, formData);
      if (response.data.success) {
        toast.success('Lead status created successfully');
        setShowCreateDialog(false);
        resetForm();
        fetchStatuses();
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create lead status';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!formData.name.trim()) {
      toast.error('Status name is required');
      return;
    }
    
    setSaving(true);
    try {
      const response = await axios.put(
        `${API}/master/lead-statuses/${selectedStatus.id}?token=${token}`,
        formData
      );
      if (response.data.success) {
        toast.success('Lead status updated successfully');
        setShowEditDialog(false);
        setSelectedStatus(null);
        resetForm();
        fetchStatuses();
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update lead status';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      const response = await axios.delete(
        `${API}/master/lead-statuses/${selectedStatus.id}?token=${token}`
      );
      if (response.data.success) {
        toast.success('Lead status deleted successfully');
        setShowDeleteDialog(false);
        setSelectedStatus(null);
        fetchStatuses();
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to delete lead status';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleSetDefault = async (status) => {
    try {
      const response = await axios.post(
        `${API}/master/lead-statuses/${status.id}/set-default?token=${token}`
      );
      if (response.data.success) {
        toast.success(response.data.message);
        fetchStatuses();
      }
    } catch (error) {
      toast.error('Failed to set default status');
    }
  };

  const handleToggleActive = async (status) => {
    try {
      const response = await axios.put(
        `${API}/master/lead-statuses/${status.id}?token=${token}`,
        { is_active: !status.is_active }
      );
      if (response.data.success) {
        toast.success(`Status ${status.is_active ? 'deactivated' : 'activated'}`);
        fetchStatuses();
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const openEditDialog = (status) => {
    setSelectedStatus(status);
    setFormData({
      name: status.name,
      description: status.description || '',
      color: status.color || '#3B82F6',
      sort_order: status.sort_order || 0,
      is_active: status.is_active
    });
    setShowEditDialog(true);
  };

  const openDeleteDialog = (status) => {
    setSelectedStatus(status);
    setShowDeleteDialog(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      color: '#3B82F6',
      sort_order: 0,
      is_active: true
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="p-6 space-y-6" data-testid="lead-status-management">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Tag className="h-6 w-6 text-blue-500" />
            Lead Status Management
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            Manage lead statuses used across the CRM
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={syncFromHubSpot}
            variant="outline"
            disabled={syncing}
            className="border-orange-300 text-orange-600 hover:bg-orange-50"
            data-testid="sync-hubspot-btn"
          >
            {syncing ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Sync from HubSpot
          </Button>
          <Button
            onClick={() => {
              resetForm();
              setShowCreateDialog(true);
            }}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="create-status-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Status
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search statuses..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={includeInactive}
                onCheckedChange={setIncludeInactive}
                id="include-inactive"
              />
              <Label htmlFor="include-inactive" className="text-sm text-slate-600 dark:text-slate-400">
                Include Inactive
              </Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Status List */}
      <Card>
        <CardHeader>
          <CardTitle>Lead Statuses ({total})</CardTitle>
          <CardDescription>
            Configure the statuses available for leads in your CRM
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          ) : statuses.length === 0 ? (
            <div className="text-center py-12">
              <Tag className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No lead statuses found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-700">
                    <th className="text-left py-3 px-4 font-medium text-slate-600 dark:text-slate-400">Status</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-600 dark:text-slate-400">Description</th>
                    <th className="text-center py-3 px-4 font-medium text-slate-600 dark:text-slate-400">Color</th>
                    <th className="text-center py-3 px-4 font-medium text-slate-600 dark:text-slate-400">Order</th>
                    <th className="text-center py-3 px-4 font-medium text-slate-600 dark:text-slate-400">Status</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-600 dark:text-slate-400">Created</th>
                    <th className="text-center py-3 px-4 font-medium text-slate-600 dark:text-slate-400">HubSpot</th>
                    <th className="text-right py-3 px-4 font-medium text-slate-600 dark:text-slate-400">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {statuses.map((status) => (
                    <tr
                      key={status.id}
                      className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50"
                      data-testid={`status-row-${status.id}`}
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-900 dark:text-white">
                            {status.name}
                          </span>
                          {status.is_default && (
                            <Badge variant="secondary" className="bg-amber-100 text-amber-700">
                              <Star className="h-3 w-3 mr-1" />
                              Default
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-slate-600 dark:text-slate-400 max-w-xs truncate">
                        {status.description || '-'}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <div
                          className="w-6 h-6 rounded-full mx-auto border-2 border-white shadow-sm"
                          style={{ backgroundColor: status.color }}
                          title={status.color}
                        />
                      </td>
                      <td className="py-3 px-4 text-center text-slate-600 dark:text-slate-400">
                        {status.sort_order}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <Badge
                          variant={status.is_active ? 'default' : 'secondary'}
                          className={status.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}
                        >
                          {status.is_active ? (
                            <><CheckCircle className="h-3 w-3 mr-1" /> Active</>
                          ) : (
                            <><AlertCircle className="h-3 w-3 mr-1" /> Inactive</>
                          )}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-slate-600 dark:text-slate-400">
                        {formatDate(status.created_at)}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {status.hubspot_value ? (
                          <Badge variant="secondary" className="bg-orange-100 text-orange-700">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Synced
                          </Badge>
                        ) : (
                          <span className="text-slate-400 text-xs">-</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-end gap-2">
                          {!status.is_default && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleSetDefault(status)}
                              title="Set as default"
                            >
                              <Star className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleActive(status)}
                            title={status.is_active ? 'Deactivate' : 'Activate'}
                          >
                            {status.is_active ? (
                              <AlertCircle className="h-4 w-4" />
                            ) : (
                              <CheckCircle className="h-4 w-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(status)}
                            data-testid={`edit-status-${status.id}`}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          {!status.is_default && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openDeleteDialog(status)}
                              className="text-red-500 hover:text-red-700"
                              data-testid={`delete-status-${status.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
              <p className="text-sm text-slate-500">
                Showing page {page} of {totalPages}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Lead Status</DialogTitle>
            <DialogDescription>
              Add a new status option for leads in your CRM
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Status Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Qualified"
                data-testid="status-name-input"
              />
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional description..."
                rows={2}
              />
            </div>
            <div>
              <Label>Color</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {COLOR_OPTIONS.map((color) => (
                  <button
                    key={color.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, color: color.value })}
                    className={`w-8 h-8 rounded-full border-2 transition-all ${
                      formData.color === color.value
                        ? 'border-slate-900 dark:border-white scale-110'
                        : 'border-transparent hover:scale-105'
                    }`}
                    style={{ backgroundColor: color.value }}
                    title={color.label}
                  />
                ))}
              </div>
            </div>
            <div>
              <Label htmlFor="sort_order">Sort Order</Label>
              <Input
                id="sort_order"
                type="number"
                value={formData.sort_order}
                onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                min={0}
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                id="is_active"
              />
              <Label htmlFor="is_active">Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={saving} data-testid="save-status-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create Status
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Lead Status</DialogTitle>
            <DialogDescription>
              Update the status details
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-name">Status Name *</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Qualified"
              />
            </div>
            <div>
              <Label htmlFor="edit-description">Description</Label>
              <Textarea
                id="edit-description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional description..."
                rows={2}
              />
            </div>
            <div>
              <Label>Color</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {COLOR_OPTIONS.map((color) => (
                  <button
                    key={color.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, color: color.value })}
                    className={`w-8 h-8 rounded-full border-2 transition-all ${
                      formData.color === color.value
                        ? 'border-slate-900 dark:border-white scale-110'
                        : 'border-transparent hover:scale-105'
                    }`}
                    style={{ backgroundColor: color.value }}
                    title={color.label}
                  />
                ))}
              </div>
            </div>
            <div>
              <Label htmlFor="edit-sort_order">Sort Order</Label>
              <Input
                id="edit-sort_order"
                type="number"
                value={formData.sort_order}
                onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                min={0}
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                id="edit-is_active"
              />
              <Label htmlFor="edit-is_active">Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdate} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Lead Status</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{selectedStatus?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={saving}
              data-testid="confirm-delete-btn"
            >
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete Status
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
